import React, { ReactElement, useState } from 'react';
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Formik, Form } from 'formik';
import ReplyIcon from '@mui/icons-material/Reply';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSecondaryButtonOutlined,
} from 'shared/components/AppComponents';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

export interface IComment {
  _id: string;
  userId: {
    _id: string;
    name: string;
  };
  organizationId: string;
  entityId: string;
  entityType: string;
  content: string;
  parentCommentId: string | null;
  isDeleted: boolean;
  isEdited: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  replies?: IComment[];
}

interface CommentCardProps {
  comment: IComment;
  onReply: (commentId: string) => void;
  activeReplyId: string | null;
  onSubmitReply: (commentId: string, text: string) => void;
  onEditComment?: (commentId: string, newContent: string) => void;
  onDeleteComment?: (commentId: string) => void;
}

const CommentCard = ({
  comment,
  onReply,
  activeReplyId,
  onSubmitReply,
  onEditComment,
  onDeleteComment,
}: CommentCardProps): ReactElement => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const formatTimeAgo = (createdAt: string): string => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'yesterday';
    return `${diffDays} days ago`;
  };

  const getAvatarInitial = (): string => {
    if (comment.userId?.name) {
      return comment.userId?.name?.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getAuthorName = (): string => {
    if (comment.userId?.name) {
      return comment.userId.name;
    }
    return 'Anonymous User';
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setMenuAnchor(null);
  };

  const handleEditSubmit = (values: { edit: string }): void => {
    if (values.edit.trim() && onEditComment) {
      onEditComment(comment._id, values.edit);
      setIsEditing(false);
    }
  };

  const handleDelete = (): void => {
    if (onDeleteComment) {
      onDeleteComment(comment._id);
    }
    handleMenuClose();
  };

  const handleReplySubmit = (
    values: { reply: string },
    resetForm: () => void
  ): void => {
    if (values.reply.trim()) {
      onSubmitReply(comment._id, values.reply);
      resetForm();
      onReply('');
    }
  };

  const isReplying = activeReplyId === comment._id;

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: '#d1f27a',
                color: '#06362b',
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              {getAvatarInitial()}
            </Avatar>
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="text.primary"
              >
                {getAuthorName()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatTimeAgo(comment.createdAt)}
                {comment.isEdited && <span> (edited)</span>}
              </Typography>
            </Box>
          </Stack>

          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreHorizIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
          >
            {orgACLConfig.aiInventory?.includes(
              'aiInventory.aiSystem.aiInventory.view.comments.editComment'
            ) && (
              <MenuItem
                onClick={() => {
                  setIsEditing(true);
                  handleMenuClose();
                }}
                dense
              >
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                Edit
              </MenuItem>
            )}
            {orgACLConfig.aiInventory?.includes(
              'aiInventory.aiSystem.aiInventory.view.comments.deleteComment'
            ) && (
              <MenuItem
                onClick={handleDelete}
                dense
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            )}
          </Menu>
        </Stack>

        {isEditing ? (
          <Box sx={{ mt: 1.5 }}>
            <Formik
              initialValues={{ edit: comment.content }}
              onSubmit={handleEditSubmit}
            >
              {({ handleSubmit, isSubmitting, values }) => (
                <Form onSubmit={handleSubmit}>
                  <AppFormikTextField
                    name="edit"
                    placeholder="Edit your comment..."
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={1}
                    mt={1}
                  >
                    <AppPrimaryButtonOutlined
                      title="Cancel"
                      className="btn-small"
                      onClick={() => setIsEditing(false)}
                    />
                    <AppPrimaryButton
                      type="submit"
                      title="Save"
                      className="btn-small"
                      disabled={isSubmitting || !values.edit?.trim()}
                    />
                  </Stack>
                </Form>
              )}
            </Formik>
          </Box>
        ) : (
          <Typography variant="body2" color="text.primary" sx={{ py: 3 }}>
            {comment.content}
          </Typography>
        )}

        {!isEditing &&
          orgACLConfig.aiInventory?.includes(
            'aiInventory.aiSystem.aiInventory.view.comments.replyComment'
          ) && (
            <AppSecondaryButtonOutlined
              title="Reply"
              startIcon={<ReplyIcon />}
              onClick={() => onReply(comment._id)}
              className="btn-small"
            />
          )}

        {isReplying && (
          <Box sx={{ mt: 2 }}>
            <Formik
              initialValues={{ reply: '' }}
              onSubmit={(values, { resetForm }) =>
                handleReplySubmit(values, resetForm)
              }
            >
              {({ handleSubmit, isSubmitting, values }) => (
                <Form onSubmit={handleSubmit}>
                  <AppFormikTextField
                    name="reply"
                    placeholder="Write a reply..."
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={1}
                    mt={1}
                  >
                    <AppPrimaryButtonOutlined
                      title="Cancel"
                      className="btn-small"
                      onClick={() => onReply('')}
                    />
                    <AppPrimaryButton
                      type="submit"
                      title="Reply"
                      className="btn-small"
                      disabled={isSubmitting || !values.reply?.trim()}
                    />
                  </Stack>
                </Form>
              )}
            </Formik>
          </Box>
        )}
      </Paper>

      {comment.replies && comment.replies.length > 0 && (
        <Stack spacing={1.5} sx={{ mt: 1.5, ml: { xs: 2, sm: 4 } }}>
          {comment.replies.map((reply) => (
            <CommentCard
              key={reply._id}
              comment={reply}
              onReply={onReply}
              activeReplyId={activeReplyId}
              onSubmitReply={onSubmitReply}
              onEditComment={onEditComment}
              onDeleteComment={onDeleteComment}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default CommentCard;
